import { Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';

import { HomeCarousel } from 'src/app/services/inicio.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { MainServices } from 'src/app/services/MainServices';
import { apiErrorMessage } from 'src/app/services/response.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { faChevronLeft, faChevronRight } from 'src/app/icons/solid';
import data from 'src/data';

interface Slide {
  titulo: string | null;
  descripcion: string | null;
  sources: Array<Source>;
}

interface HeroSlide {
  badge: string;
  titulo: string[];
  descripcion: string;
  ctaLabel: string;
  ctaLink: string;
  external: boolean;
  image: string;
}

interface Source {
  media: string;
  src: string;
  type: string;
}

const pathInfoRegex = /^(?<path>.*)\/(?<filename>.*?)(?<ext>\..+)?$/;
function pathInfo(url: string): { path: string, filename: string, ext: string; } {
  return (pathInfoRegex.exec(url)?.groups as any) ?? { path: '', filename: '', ext: '' };
}

function transformSources({ url, sources }: { url: string, sources: HomeCarousel['sources']; }) {
  return sources
    .sort((a, b) => (a.width < b.width ? 1 : a.width > b.width ? -1 : 0))
    .reduce((sources, source, index, array) => {
      const media = [];
      const minWidth = array[index + 1] ? array[index + 1].width : 0;
      if (minWidth) {
        media.push(`(min-width: ${minWidth}px)`);
      }

      media.push(`(orientation: ${source.portrait ? 'portrait' : 'landscape'})`);

      const { width, types } = source;
      const { path, filename } = pathInfo(url);

      for (const type of types) {
        sources.push({
          src: `${path}/${filename}-${source.portrait ? 'port' : 'land'}@${width}.${type}`,
          media: media.join(' and '),
          type: `image/${type}`,
        });
      }

      return sources;
    }, [] as Array<Source>);
}

function transformSlides(slides: Array<HomeCarousel>) {
  return slides.map((slide) => ({
    titulo: slide.titulo,
    descripcion: slide.descripcion,
    sources: transformSources(slide),
  }));
}

@Component({
    selector: 'app-inicio-banner',
    templateUrl: './inicio-banner.component.html',
    styleUrls: ['./inicio-banner.component.scss'],
    standalone: false
})
export class InicioBannerComponent implements OnInit, OnDestroy {
  public slides: Array<Slide> = transformSlides(data.banner);
  public heroSlides: Array<HeroSlide> = [
    {
      badge: 'Nueva Sucursal',
      titulo: ['MÁS CERCA DE TI', 'EN TLALNEPANTLA'],
      descripcion: 'Ahora puedes realizar tus envíos desde nuestra nueva sucursal en Tlalnepantla, con el respaldo, cobertura y seguridad.',
      ctaLabel: 'Ver sucursales',
      ctaLink: '/cobertura',
      external: false,
      image: 'assets/images/slide-4.webp',
    },
    {
      badge: 'Tu Socio Logístico en México',
      titulo: ['Tu carga segura,', 'a tiempo, siempre.'],
      descripcion: 'Red nacional de paquetería y mensajería con cobertura en toda la República. Trazabilidad en tiempo real y entregas que cumplen tu operación.',
      ctaLabel: 'Comprar guías en línea',
      ctaLink: 'https://pmm.com.mx/tiendapmm',
      external: true,
      image: 'assets/images/slide-1.webp',
    },
    {
      badge: 'Guías Prepagadas',
      titulo: ['Envía más,', 'paga menos.'],
      descripcion: 'Adquiere tus guías por adelantado y accede a tarifas preferenciales. La opción más eficiente para negocios con volumen regular de envíos.',
      ctaLabel: 'Ver guías disponibles',
      ctaLink: 'https://pmm.com.mx/tiendapmm',
      external: true,
      image: 'assets/images/slide-2.webp',
    },
    {
      badge: 'Soluciones Empresariales',
      titulo: ['Logística diseñada', 'para tu empresa.'],
      descripcion: 'Recolección a domicilio, control de inventario por pieza y reportes de entrega personalizados. Infraestructura logística que escala con tu negocio.',
      ctaLabel: 'Cotizar ahora',
      ctaLink: '/cotizador',
      external: false,
      image: 'assets/images/slide-3.webp',
    },
  ];
  public currentSlide = 0;
  private autoPlayInterval: any;
  public icons = {
    previous: faChevronLeft,
    next: faChevronRight,
  };
  
  // Tracking form
  public config = data.config;
  public rastreoForm: UntypedFormGroup;
  private submitted = false;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private scrollService: ScrollService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private services: MainServices,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.startAutoPlay();
    this.initRastreoForm();
  }

  private initRastreoForm() {
    this.rastreoForm = this.formBuilder.group({
      numrastreo: ['', [
        Validators.required,
        Validators.minLength(this.config.minbusqueda),
        Validators.maxLength(this.config.maxbusqueda),
      ]],
    });
  }

  ngOnDestroy() {
    this.stopAutoPlay();
  }

  startAutoPlay() {
    this.autoPlayInterval = setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.heroSlides.length;
  }

  prevSlide() {
    this.currentSlide = this.currentSlide === 0 ? this.heroSlides.length - 1 : this.currentSlide - 1;
  }

  goToSlide(index: number) {
    this.currentSlide = index;
    this.stopAutoPlay();
    this.startAutoPlay();
  }

  bajar(event: MouseEvent) {
    const element = this.elRef.nativeElement;
    this.scrollService.scrollTo(element.offsetHeight + element.offsetTop - 62);
    event.preventDefault();

    return false;
  }

  // Tracking functionality
  async onSubmitRastreo() {
    this.submitted = true;
    this.spinnerService.show();
    
    if (!this.rastreoForm.valid) { 
      this.spinnerService.hide();
      return; 
    }
    
    const numRastreo = this.rastreoForm.value.numrastreo;
    
    try {
      // GET /rastreo?numero= devuelve la carga util directa; los errores
      // (404 / 409 / 422) llegan como rechazo con su codigo HTTP real.
      const rastreo = await this.services.getRastroGuia(numRastreo);

      if (rastreo.cancelado === true) {
        (await import('sweetalert2')).default.fire(
          this.translate.instant('main.error'),
          'Número de rastreo no válido',
          'error'
        );
      } else {
        localStorage.setItem('config', JSON.stringify(this.config));
        localStorage.setItem('numRastreo', JSON.stringify(numRastreo));
        localStorage.setItem('rastreo', JSON.stringify(rastreo));
        this.router.navigate(['/rastreo']);
      }
    } catch (error) {
      (await import('sweetalert2')).default.fire(
        this.translate.instant('main.error'),
        apiErrorMessage(error, this.translate.instant('rastreo.error_searching')),
        'error'
      );
    } finally {
      this.spinnerService.hide();
    }
  }
}
